/**
 * Compound unit strings: `V`, `mV`, `1/°C`, `kg*m/s^2`, `N·m`, `m²`.
 *
 * A unit field is free text and always will be — the tool is domain-agnostic
 * and people type `pcs`, `counts`, `LSB`. So this parser has exactly one hard
 * requirement: never guess. Anything it does not fully recognise returns null,
 * the quantity becomes unknown, and every check touching it is skipped. A unit
 * checker that produces false positives on ordinary text gets switched off, and
 * then it protects nobody.
 */

import { DIMENSIONLESS, dimMul, dimDiv, dimPow, type Dimension } from './dimension.ts';
import { resolveSymbol, type Unit } from './registry.ts';

/** Superscript digits, so `m²` and `s⁻¹` parse like `m^2` and `s^-1`. */
const SUPERSCRIPTS: Record<string, string> = {
  '⁻': '-',
  '⁰': '0',
  '¹': '1',
  '²': '2',
  '³': '3',
  '⁴': '4',
  '⁵': '5',
  '⁶': '6',
  '⁷': '7',
  '⁸': '8',
  '⁹': '9',
};

const IDENTITY: Unit = { dimension: DIMENSIONLESS, scale: 1 };

interface Cursor {
  src: string;
  i: number;
}

function skipSpace(c: Cursor): void {
  while (c.i < c.src.length && /\s/.test(c.src[c.i])) c.i++;
}

/** An integer or decimal exponent, e.g. the `2` in `s^2` or `-1` in `s^-1`. */
function readExponent(c: Cursor): number | null {
  skipSpace(c);
  const start = c.i;
  if (c.src[c.i] === '+' || c.src[c.i] === '-') c.i++;
  while (c.i < c.src.length && /[\d.]/.test(c.src[c.i])) c.i++;
  if (c.i === start) return null;
  const n = Number(c.src.slice(start, c.i));
  return Number.isFinite(n) ? n : null;
}

function readAtom(c: Cursor): Unit | null {
  skipSpace(c);
  if (c.i >= c.src.length) return null;

  if (c.src[c.i] === '(') {
    c.i++;
    const inner = readExpression(c);
    if (inner === null) return null;
    skipSpace(c);
    if (c.src[c.i] !== ')') return null;
    c.i++;
    return inner;
  }

  // A bare number: only `1` is meaningful, as the numerator of `1/°C`. Any
  // other literal would be a conversion factor baked into the unit string,
  // which is exactly the kind of guessing this parser refuses to do.
  const numStart = c.i;
  while (c.i < c.src.length && /[\d.]/.test(c.src[c.i])) c.i++;
  if (c.i > numStart) {
    return Number(c.src.slice(numStart, c.i)) === 1 ? IDENTITY : null;
  }

  // A symbol: letters plus the handful of non-ASCII characters units use.
  const symStart = c.i;
  while (c.i < c.src.length && /[A-Za-zΩµμ°]/.test(c.src[c.i])) c.i++;
  if (c.i === symStart) return null;

  const resolved = resolveSymbol(c.src.slice(symStart, c.i));
  return resolved && { dimension: resolved.dimension, scale: resolved.scale };
}

function readFactor(c: Cursor): Unit | null {
  const atom = readAtom(c);
  if (atom === null) return null;

  skipSpace(c);
  if (c.src[c.i] !== '^') return atom;
  c.i++;

  const n = readExponent(c);
  if (n === null) return null;
  return { dimension: dimPow(atom.dimension, n), scale: Math.pow(atom.scale, n) };
}

function readExpression(c: Cursor): Unit | null {
  let acc = readFactor(c);
  if (acc === null) return null;

  for (;;) {
    skipSpace(c);
    const op = c.src[c.i];
    if (op !== '*' && op !== '·' && op !== '/') return acc;
    c.i++;

    const rhs = readFactor(c);
    if (rhs === null) return null;

    acc =
      op === '/'
        ? { dimension: dimDiv(acc.dimension, rhs.dimension), scale: acc.scale / rhs.scale }
        : { dimension: dimMul(acc.dimension, rhs.dimension), scale: acc.scale * rhs.scale };
  }
}

/**
 * Parse a unit string into a dimension and a scale, or null when any part of it
 * is unrecognised.
 *
 * Null is not a failure to be reported — it is the normal outcome for `%`,
 * `psi`, `pcs` and every other label the tool has no physics for, and it simply
 * removes that quantity from checking.
 */
export function parseUnit(text: string | undefined): Unit | null {
  const src = text?.trim();
  if (!src) return null;

  // Normalise superscripts to `^n` so `m²` and `m^2` share one path.
  let normalised = '';
  for (const ch of src) {
    const digit = SUPERSCRIPTS[ch];
    if (digit === undefined) {
      normalised += ch;
      continue;
    }
    // Open an exponent unless we are already inside one.
    if (!/\^[-\d.]*$/.test(normalised)) normalised += '^';
    normalised += digit;
  }

  const c: Cursor = { src: normalised, i: 0 };
  const unit = readExpression(c);
  if (unit === null) return null;

  skipSpace(c);
  return c.i === c.src.length ? unit : null;
}

/** The dimension of a unit string, or null when unrecognised. */
export function unitDimension(text: string | undefined): Dimension | null {
  return parseUnit(text)?.dimension ?? null;
}
